import axios from 'axios';
import {
  API_CALL_METHODS,
  FILE_UPLOADER_DATA,
  FETCH_DATA_OK_STATUS,
  FETCH_DATA_ERROR_STATUS,
} from '@/utils/file-uploader-utils/file-uploader-utils';

export const FilesUploaderStateManager = {
  namespaced: true,

  state: {
    listFilesInState: [],
    isLoadingFilesState: false,
    fileOnDeletionState: [],
    isDownloadingFile: [],
    paginationInfo: { currentPage: 1, totalPages: null, perPage: null, lastPage: null },
    canScrollToBottom: false,
    isComponentMounted: false,
    downloadURL: null,
    fileAlreadyExists: false,
    fileTooBig: false,
  },

  getters: {
    filesList(state) {
      return state.listFilesInState;
    },

    getCurrentPaginationInfo(state) {
      return state.paginationInfo;
    },

    getDownloadURL(state) {
      return state.downloadURL;
    },
  },

  mutations: {
    setFilesList(state, fileListToAdd) {
      const newListReportFilesUpdated = state.listFilesInState;
      const filteredFiles = fileListToAdd.filter(
        (itemToAdd) => !state.listFilesInState.some((existingItemInArray) => existingItemInArray.id === itemToAdd.id)
      );

      newListReportFilesUpdated.push(...filteredFiles);
      const paginationInfoUpdated = { ...state.paginationInfo, perPage: newListReportFilesUpdated.length, lastPage: 1 };

      state.listFilesInState = newListReportFilesUpdated;
      state.paginationInfo = paginationInfoUpdated;
    },

    setLoaderOnListFiles(state, isLoadingFilesState) {
      state.isLoadingFilesState = isLoadingFilesState;
    },

    setUpdatedList(state, id) {
      let stateObject = [...state.listFilesInState];

      stateObject = stateObject.filter((file) => file.id != id);
      state.listFilesInState = stateObject;
    },

    setPaginationInfo(state, { current_page, last_page, per_page, total }) {
      const paginationInfoUpdated = {
        ...state.paginationInfo,
        currentPage: current_page,
        totalPages: total,
        perPage: per_page,
        lastPage: last_page,
      };

      state.paginationInfo = paginationInfoUpdated;
    },

    setDeletingFile(state, { status, id }) {
      const filesOnDeleteProcess = [...state.fileOnDeletionState];
      const index = filesOnDeleteProcess.findIndex((item) => item.id === id);

      if (status) {
        if (index === -1) {
          filesOnDeleteProcess.push({ status, id });
        }
      } else {
        filesOnDeleteProcess.splice(index, 1);
      }

      state.fileOnDeletionState = filesOnDeleteProcess;
    },

    setIsDownloadingFile(state, { status, id }) {
      const stateObject = [...state.isDownloadingFile];
      const index = stateObject.findIndex((item) => item.id === id);

      if (status) {
        if (index === -1) {
          stateObject.push({ status, id });
        }
      } else stateObject.splice(index, 1);

      state.isDownloadingFile = stateObject;
    },

    setScrollToBottom(state, boolean) {
      state.canScrollToBottom = boolean;
    },

    setComponentIsMounted(state, boolean) {
      state.isComponentMounted = boolean;
    },

    setDownloadUrlLink(state, downloadURLData) {
      state.downloadURL = downloadURLData;
    },

    setListReset(state) {
      state.listFilesInState = [];
    },

    setFileAlreadyExists(state, boolean) {
      state.fileAlreadyExists = boolean;
    },

    setFileTooBig(state, boolean) {
      state.fileTooBig = boolean;
    },
  },

  actions: {
    componentMountedAction({ commit }, payload) {
      commit('setComponentIsMounted', payload);
    },

    async deleteAFileAction({ commit }, { repository, project, report, id, uuid, origin, site }) {
      const { endpoint, params } = FILE_UPLOADER_DATA[origin].action.deleteFiles;
      const apiParams = params({ repository, project, report, reportFile: id, id, uuid, site });

      commit('setDeletingFile', { status: true, id: id });

      try {
        const responseDeletion = await this._vm.$api(endpoint, apiParams);

        if (FETCH_DATA_OK_STATUS.includes(responseDeletion.status)) {
          commit('setUpdatedList', id);
        }
      } catch (error) {
        console.error(error);
      } finally {
        commit('setDeletingFile', { status: false, id: id });
      }
    },

    async downloadFileAction({ commit }, { repository, project, report, id, name, filename, site, uuid, origin }) {
      commit('setIsDownloadingFile', { status: true, id: id });

      try {
        const { endpoint, params, apiCallMethod, downloadURLResponse } =
          FILE_UPLOADER_DATA[origin].action.downloadFiles;
        const apiParams = params({ repository, project, report, reportFile: id, id, uuid, site });
        const responseDownload =
          (apiCallMethod && (await this._vm[apiCallMethod](endpoint, apiParams).url())) ||
          (await this._vm[API_CALL_METHODS.api](endpoint, apiParams));

        if (responseDownload || FETCH_DATA_OK_STATUS.includes(responseDownload.status)) {
          commit('setDownloadUrlLink', {
            url: downloadURLResponse(responseDownload),
            fileName: name || filename,
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        commit('setIsDownloadingFile', { status: false, id: id });
      }
    },

    async listFilesAction({ commit, dispatch }, { origin, site, repository, project, report, page, perPage }) {
      const { endpoint, params, metaPagination } = FILE_UPLOADER_DATA[origin]?.action.listFiles;
      const apiParams = params({ repository, project, report, site, page, perPage });

      commit('setLoaderOnListFiles', true);

      try {
        const responseList = await this._vm.$api(endpoint, apiParams);

        commit('setFilesList', responseList.data.data);
        if (metaPagination) commit('setPaginationInfo', responseList.data.meta);

        if (FETCH_DATA_OK_STATUS.includes(responseList?.status)) {
          commit('setLoaderOnListFiles', false);
          dispatch('componentMountedAction', true);
        }
      } catch (error) {
        console.error(error);
        commit('setLoaderOnListFiles', false);
      }
    },

    resetScrollToBottom({ commit }) {
      commit('setScrollToBottom', false);
    },

    async uploadFileAction(
      { commit, dispatch, getters },
      { origin, site, repository, project, report, page, data, files }
    ) {
      const itemsPerPage = getters.getCurrentPaginationInfo.perPage
        ? getters.getCurrentPaginationInfo.perpage + data.length
        : data.length;
      const { endpoint, params, specificComponentUploadProcess } = FILE_UPLOADER_DATA[origin].action.uploadFiles;
      const apiParams = params({ site, repository, project, report, page, data, files });

      commit('setLoaderOnListFiles', true);
      try {
        if (specificComponentUploadProcess) {
          let requestAttempt = 0;

          for (const { file } of data) {
            if (!file) throw new Error('File not found');

            const formData = new FormData();

            formData.append('file', file);

            const responseUpload = await this._vm.$api(endpoint, apiParams, formData);

            requestAttempt++;

            if (FETCH_DATA_OK_STATUS.includes(responseUpload?.status) && requestAttempt === data.length) {
              await dispatch('listFilesAction', { ...apiParams, origin });

              break;
            }
          }
        }

        const responseUpload = await this._vm.$api(endpoint, apiParams);

        if (FETCH_DATA_OK_STATUS.includes(responseUpload?.status)) {
          await dispatch('saveDataOnS3BucketsAction', {
            repository,
            project,
            report,
            data,
            response: responseUpload.data.data,
            perPage: itemsPerPage || 20,
            page,
            origin,
          });
          commit('setLoaderOnListFiles', false);
        }
      } catch (error) {
        console.error(error);
        commit('setLoaderOnListFiles', false);
      }
    },

    async saveDataOnS3BucketsAction(
      { dispatch, commit },
      { repository, project, report, data, response, perPage, origin }
    ) {
      commit('setFileTooBig', false);
      try {
        let requestAttempt = 0;

        for (const { url, path, fields } of response) {
          const urlPresigned = `${url}`;
          const file = data.find(({ file }) => path.includes(file.name));
          const formData = new FormData();

          if (!file) throw new Error('File not found');

          Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
          formData.append('file', file.file);

          const responseCall = await axios.post(urlPresigned, formData, {
            withCredentials: false,
          });

          requestAttempt++;

          if (FETCH_DATA_OK_STATUS.includes(responseCall?.status)) {
            await dispatch('getDownloadLinkPerFileAction', {
              repository,
              project,
              report,
              perPage,
              path,
              lastRequest: requestAttempt === response.length ? true : false,
              origin,
            });
          }
        }
      } catch (error) {
        console.error(error);
        if (error.response?.status !== 400) return;
        commit('setFileTooBig', true);
      }
    },

    async getDownloadLinkPerFileAction(
      { dispatch, commit },
      { site, repository, project, report, perPage, path, lastRequest, origin }
    ) {
      commit('setFileAlreadyExists', false);
      try {
        const body = { files: [path] };

        const response = await this._vm.$api(
          'repositories.projects.reports.files.create',
          {
            version: '1.0',
            repository,
            project,
            report,
          },
          body
        );

        if (FETCH_DATA_OK_STATUS.includes(response?.status) && lastRequest) {
          await dispatch('listFilesAction', { site, repository, project, report, perPage, origin });

          commit('setScrollToBottom', true);
        }
      } catch (error) {
        console.error(error);
        if (!FETCH_DATA_ERROR_STATUS.includes(error.response?.status)) return;
        commit('setFileAlreadyExists', true);
      }
    },

    resetListAction({ commit }) {
      commit('setListReset');
    },
  },
};
